<template>
  <div class="address" :class="{ 'address--coordinates': showMap, 'address--disabled': disabled }">
    <div class="address__inputs">
      <mapbox-address-autofill data-field="street" ref="autofill" :access-token="access_token">
        <InputComponent
          v-model="value.street"
          :label="labels.street"
          :disabled="disabled"
          v-bind="fieldProps.street"
          autocomplete="address-line1"
        />
      </mapbox-address-autofill>

      <InputComponent
        v-model="value.postcode"
        :label="labels.postcode"
        data-field="postcode"
        :disabled="disabled"
        v-bind="fieldProps.postcode"
        autocomplete="postal-code"
      />
      <InputComponent
        v-model="value.city"
        :label="labels.city"
        :disabled="disabled"
        data-field="city"
        v-bind="fieldProps.city"
        autocomplete="address-level2"
      />
      <InputComponent
        v-model="value.county"
        :label="labels.county"
        :disabled="disabled"
        data-field="county"
        v-bind="fieldProps.county"
      />
      <Alert v-if="showMap && !disabled" data-field="addressAlert">
        <i18n path="autocompleteAddressAlert" tag="span" />
      </Alert>
    </div>

    <InputComponent v-if="showMap" :label="labels.position" class="address__map-wrapper" data-field="position">
      <div class="grow-1" ref="map"></div>
    </InputComponent>
  </div>
</template>

<script>
import { InputComponent } from 'vue-elder-input'
import { mapi18n } from '@/i18n'
import { AlertComponent as Alert } from 'vue-elder-alert'
import MapComponent from '@/components/Map'
import mapboxgl from 'mapbox-gl'
import { MapboxAddressAutofill as Autofill } from '@mapbox/search-js-web'

import 'mapbox-gl/dist/mapbox-gl.css'

export default {
  props: {
    value: {
      type: Object,
      default: () => ({}),
      required: true,
    },
    language: {
      type: String,
      default: 'nb',
    },

    disabled: Boolean,
    validator: Object,
    validatorPrefix: {
      type: String,
      default: '',
    },
    showMap: {
      type: Boolean,
      default: true,
    },
  },
  watch: {
    showMap: {
      handler() {
        this.$nextTick(() => this.createMap())
      },
      immediate: true,
    },
    center: {
      handler(val) {
        if (!this.marker || !this.map) return
        this.marker.setLngLat(val)
        this.map.flyTo({
          center: val,
        })
      },
      immediate: true,
    },
    disabled(val) {
      if (!this.marker) return
      this.marker.setDraggable(val)
    },
  },
  data() {
    return {
      loading: false,
      location: '',
      access_token: process.env.VUE_APP_MAPBOX_ACCESS_TOKEN,
      map: null,
      autofill: null,
      marker: null,
    }
  },
  computed: {
    fieldProps() {
      if (!this.validator) return {}

      return Object.fromEntries(
        ['street', 'city', 'postcode', 'county'].map(key => [
          key,
          {
            required: this.validator.fields[[this.validatorPrefix, key].join('.')].rule.includes('required'),
            isValid: this.validator.fields[[this.validatorPrefix, key].join('.')].valid,
            validationMessage: this.validator.fields[[this.validatorPrefix, key].join('.')].error,
          },
        ]),
      )
    },
    center() {
      if (this.lng && this.lat) return [this.lng, this.lat]
      return [10.75278, 59.91111]
    },
    lng: {
      get() {
        if (!this.value.location.coordinates) return 0
        return this.value.location.coordinates[0]
      },
      set(val) {
        this.value.location.coordinates = [val || 0, this.lat]
      },
    },
    lat: {
      get() {
        if (!this.value.location.coordinates) return 0
        return this.value.location.coordinates[1]
      },
      set(val) {
        this.value.location.coordinates = [this.lng, val || 0]
      },
    },
    ...mapi18n('labels', ['street', 'postcode', 'city', 'county', 'latitude', 'longitude', 'position']),
  },
  methods: {
    async createMap() {
      if (!this.showMap) return
      try {
        mapboxgl.accessToken = this.access_token
        this.map = new mapboxgl.Map({
          container: this.$refs.map,
          style: process.env.VUE_APP_MAPBOX_STYLE,
          center: this.center,
          zoom: 11,
        })

        this.marker = new mapboxgl.Marker({
          draggable: !this.disabled,
          color: '#333333',
        })
          .setLngLat(this.center)
          .addTo(this.map)

        this.marker.on('dragend', e => {
          this.value.location.coordinates = Object.values(e.target._lngLat)
          this.getLocation()
        })

        this.map.addControl(new mapboxgl.FullscreenControl(), 'top-left')
        this.map.addControl(new mapboxgl.NavigationControl(), 'top-left')
      } catch (err) {
        console.log('map error', err)
      }
    },
    async getLocation() {
      const map = {
        oslo: 'Oslo',
      }

      fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${this.center[0]},${this.center[1]}.json?access_token=${this.access_token}&language=${this.language}`,
      )
        .then(response => response.json())
        .then(data => {
          this.value.postcode = data.features[1].place_name.split(',')[0]
          this.value.city = data.features[2].place_name.split(',')[0]
          this.value.county =
            map[data.features[2].place_name.split(',')[0].toLowerCase()] || data.features[3].place_name.split(',')[0]
          this.value.street = data.features[0].place_name.split(',')[0]
        })
        .catch(error => console.error(error))
    },
  },
  mounted() {
    this.autofill = new Autofill({
      input: this.$refs.input,
      accessToken: this.accessToken,
      options: {
        proximity: 'ip',
        limit: 5,
      },
    })
    this.$refs.autofill.addEventListener('retrieve', ev => {
      const feature = this.$path('detail.features.0', ev)
      let location = this.$path('geometry.coordinates', feature)

      if (!location) return

      this.value.county = this.$path('properties.region', feature) || this.$path('properties.place', feature)
      this.value.location.coordinates = location
    })
  },
  components: {
    InputComponent,
    MapComponent,
    Alert,
  },
}
</script>

<style lang="scss">
.address {
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: $form-gap;

  &--coordinates {
    grid-template-columns: repeat(2, 1fr);
  }

  &--disabled {
    [data-field='position'] {
      display: none;
    }
  }

  &__map-wrapper {
    display: flex;
    flex-direction: column;
  }

  .mapboxgl-ctrl-attrib,
  .mapboxgl-ctrl-logo,
  .mapboxgl-ctrl-top-right {
    display: none;
  }

  .mapboxgl-ctrl-top-left {
    position: relative;
    z-index: 0;
  }

  &__inputs {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;

    [data-field='street'],
    [data-field='county'],
    [data-field='addressAlert'],
    [data-field='position'] {
      grid-column-end: span 2;
    }

    [data-field='postcode'],
    [data-field='city'] {
      grid-column-end: span 1;
    }
  }
}

mapbox-search-listbox {
  [class$='--ResultsAttribution'] {
    display: none;
  }
}
</style>
